import { useState } from 'react'
import { FormHelperText, TextField } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'components/Button/Button'
import { useUser } from 'hooks/user'
import { useCallback, useMemo, useEffect } from 'react'
import React from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { PATH, PROGRESS_TYPES } from 'utils/constants'
import { emailSchema, passwordSchema } from 'utils/validationSchema'
import * as yup from 'yup'
import logoAvtodor from 'images/branding/avtodor/logo.png'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { COMPANY, CompanyName } from 'config'
import { branding } from 'utils/branding'

import styleAvtodor from '../Layout/avtodor/style.module.css'
import styleSkyeer from '../Layout/skyeer/style.module.css'

interface IFormInputs {
  email: string
  password: string
}

const Signin: React.FC = () => {
  const user = useUser()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [showPassword, setShowPassword] = useState(false)

  const loginProgress = useMemo(() => user?.state.loginProgress, [user?.state.loginProgress])
  const methods = useForm<IFormInputs>({
    resolver: yupResolver(yup.object().shape({ email: emailSchema(), password: passwordSchema() })),
  })

  const onSubmit: SubmitHandler<IFormInputs> = useCallback(
    data => {
      user?.actions.signin(data.email, data.password)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (user?.state.userLoggedIn && loginProgress === PROGRESS_TYPES.SUCCESS) {
      const nextUrl = searchParams.get('next')
      if (nextUrl) {
        try {
          const decodedUrl = decodeURIComponent(nextUrl)
          navigate(decodedUrl, { replace: true })
        } catch (error) {
          // Если URL некорректный, перенаправляем на главную
          navigate(PATH.MAIN, { replace: true })
        }
      } else {
        navigate(PATH.MAIN, { replace: true })
      }
    }
  }, [user?.state.userLoggedIn, loginProgress, searchParams, navigate])

  const SigninLayout = branding.getSigninLayout()

  const style = COMPANY === CompanyName.AVTODOR ? styleAvtodor : styleSkyeer

  return (
    <SigninLayout>
      <form onSubmit={methods.handleSubmit(onSubmit)} autoComplete="off" className={style.signinBlock}>
        {COMPANY === CompanyName.AVTODOR ? <img src={logoAvtodor} alt="logo" className={style.logo} /> : null}
        <div className={style.enterText}>{t('auth.signin')}</div>
        <div className={style.fld}>
          <TextField
            {...methods.register('email')}
            id="email"
            name="email"
            variant="standard"
            autoComplete="username"
            inputProps={{
              'data-test-id': 'email',
            }}
            aria-describedby="email-error-text"
            error={!!methods.formState.errors.email}
            label={t('auth.login')}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <FormHelperText id="email-error-text">
            {methods.formState.errors.email?.message ? t('auth.invalid_email') : ''}
          </FormHelperText>
        </div>
        <div className={style.fld}>
          <TextField
            {...methods.register('password')}
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            variant="standard"
            autoComplete="password"
            inputProps={{
              'data-test-id': 'password',
            }}
            aria-describedby="password-error-text"
            error={!!methods.formState.errors.password}
            label={t('auth.password')}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <div className={style.eye} onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </div>

          <FormHelperText id="password-error-text">
            {methods.formState.errors.password?.message ? t('auth.invalid_password') : ''}
          </FormHelperText>
        </div>
        <Link className={style.forgotPasswordLink} to={PATH.RESTORE_PASSWORD}>
          {t('auth.forgot_password')}
        </Link>
        <Button.Standart
          dataTestId={'submit-btn'}
          disabled={false}
          loading={loginProgress === PROGRESS_TYPES.WORK}
          className={style.signinButton}
        >
          {t('auth.enter')}
        </Button.Standart>
      </form>
    </SigninLayout>
  )
}

export default Signin
